import { useState, useEffect } from 'react';
import { apiRequest } from '../lib/utils';
import ImageCompare from '../components/ImageCompare';
import { Button } from '../components/ui/button';
import { ArrowRight, Camera } from 'lucide-react';
import AppointmentModal from '../components/AppointmentModal';

// Curated gallery images for dental procedures
const PLACEHOLDER_GALLERY = [
  {
    id: '1',
    before_image: 'https://images.unsplash.com/photo-1660732205543-dfef1a8761f7?crop=entropy&cs=srgb&fm=jpg&w=600',
    after_image: 'https://images.pexels.com/photos/16212691/pexels-photo-16212691.png?auto=compress&cs=tinysrgb&w=600',
    description: 'Full Denture Transformation - Complete Smile Restoration',
    category: 'Full Dentures'
  },
  {
    id: '2',
    before_image: 'https://images.unsplash.com/photo-1661438818937-437f1aeaf08e?crop=entropy&cs=srgb&fm=jpg&w=600',
    after_image: 'https://images.unsplash.com/photo-1675526607070-f5cbd71dde92?crop=entropy&cs=srgb&fm=jpg&w=600',
    description: 'Partial Denture Case - Preserving Natural Teeth',
    category: 'Partial Dentures'
  },
  {
    id: '3',
    before_image: 'https://images.unsplash.com/photo-1663182234283-28941e7612da?crop=entropy&cs=srgb&fm=jpg&w=600',
    after_image: 'https://images.unsplash.com/photo-1769559893692-c6d0623bf8e4?crop=entropy&cs=srgb&fm=jpg&w=600',
    description: 'Immediate Dentures - Same Day Smile',
    category: 'Immediate Dentures'
  },
  {
    id: '4',
    before_image: 'https://images.pexels.com/photos/6627590/pexels-photo-6627590.jpeg?auto=compress&cs=tinysrgb&w=600',
    after_image: 'https://images.pexels.com/photos/12474261/pexels-photo-12474261.jpeg?auto=compress&cs=tinysrgb&w=600',
    description: 'Denture Repair & Reline - Restored Comfort',
    category: 'Denture Repairs'
  },
];

// Senior patient before/after showcase images (side-by-side format)
const SENIOR_SHOWCASE = [
  {
    id: 'senior-1',
    image: 'https://customer-assets.emergentagent.com/job_smile-restore-coos/artifacts/xzjmbom7_senior%20full.jpg',
    description: 'Senior Male - Full Denture Transformation',
    category: 'Full Dentures'
  },
  {
    id: 'senior-2',
    image: 'https://customer-assets.emergentagent.com/job_smile-restore-coos/artifacts/dnphu405_senior%20full-2.jpg',
    description: 'Senior Male - Complete Smile Makeover',
    category: 'Full Dentures'
  },
  {
    id: 'senior-3',
    image: 'https://customer-assets.emergentagent.com/job_smile-restore-coos/artifacts/i8q97biz_senior%20woman.jpg',
    description: 'Senior Female - Denture Upgrade',
    category: 'Full Dentures'
  },
  {
    id: 'senior-4',
    image: 'https://customer-assets.emergentagent.com/job_smile-restore-coos/artifacts/s2fqt66p_senior%20woman%202.jpg',
    description: 'Senior Female - Full Denture Restoration',
    category: 'Full Dentures'
  },
  {
    id: 'senior-5',
    image: 'https://customer-assets.emergentagent.com/job_smile-restore-coos/artifacts/1ialdg41_senior%20woman%204.jpg',
    description: 'Senior Female - Natural Looking Dentures',
    category: 'Full Dentures'
  },
];

// Partial denture showcase
const PARTIAL_SHOWCASE = [
  {
    id: 'partial-1',
    image: 'https://customer-assets.emergentagent.com/job_smile-restore-coos/artifacts/dcwtv9ml_Partial-Dentures-London-before-and-after.jpg',
    description: 'Partial Denture - Missing Teeth Restored',
    category: 'Partial Dentures'
  },
];

const CATEGORIES = ['All', 'Full Dentures', 'Partial Dentures', 'Denture Repairs', 'Immediate Dentures'];

export default function GalleryPage() {
  const [gallery, setGallery] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showAppointment, setShowAppointment] = useState(false);

  useEffect(() => {
    apiRequest('/public/gallery')
      .then((data) => {
        if (data && data.length > 0) {
          setGallery(data);
        } else {
          setGallery(PLACEHOLDER_GALLERY);
        }
      })
      .catch(() => setGallery(PLACEHOLDER_GALLERY));
  }, []);

  const filteredGallery = selectedCategory === 'All'
    ? gallery
    : gallery.filter(item => item.category === selectedCategory);

  return (
    <div className="pt-24 lg:pt-32" data-testid="gallery-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Camera className="w-4 h-4" />
            Real Patient Results
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Before & After Gallery
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
            See the transformative results our patients have achieved. Drag the slider to compare before and after images.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b" data-testid="gallery-filter">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-3">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-orange-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`gallery-filter-${category.toLowerCase().replace(/\s/g, '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 lg:py-24 bg-white" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredGallery.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
              {filteredGallery.map((item) => (
                <div key={item.id} className="space-y-4" data-testid={`gallery-item-${item.id}`}>
                  <ImageCompare
                    beforeImage={item.before_image}
                    afterImage={item.after_image}
                  />
                  <div className="text-center">
                    <h3 className="font-semibold text-lg text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {item.description}
                    </h3>
                    {item.category && (
                      <span className="inline-block mt-2 text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">
                        {item.category}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-slate-500">No gallery items found for this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Partial Denture Showcase */}
      {(selectedCategory === 'All' || selectedCategory === 'Partial Dentures') && (
        <section className="py-16 lg:py-24 bg-white border-t" data-testid="partial-showcase">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Partial Denture Results
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Restore missing teeth while preserving your natural smile.
              </p>
            </div>
            <div className="max-w-2xl mx-auto">
              {PARTIAL_SHOWCASE.map((item) => (
                <div key={item.id} className="group" data-testid={`partial-item-${item.id}`}>
                  <div className="relative overflow-hidden rounded-2xl shadow-xl">
                    <img
                      src={item.image}
                      alt={item.description}
                      className="w-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 right-4 flex justify-between">
                      <span className="bg-slate-800/80 text-white text-sm font-medium px-4 py-1.5 rounded-full">Before</span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 flex justify-between">
                      <span className="bg-orange-600/90 text-white text-sm font-medium px-4 py-1.5 rounded-full">After</span>
                    </div>
                  </div>
                  <div className="text-center mt-6">
                    <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {item.description}
                    </h3>
                    <span className="inline-block mt-2 text-sm bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full">
                      {item.category}
                    </span>
                    <p className="mt-4 text-slate-500 text-sm">Individual results may vary</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Senior Patient Showcase - Real Before/After Results */}
      {(selectedCategory === 'All' || selectedCategory === 'Full Dentures') && (
        <section className="py-16 lg:py-24 bg-slate-50" data-testid="senior-showcase">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Senior Patient Transformations
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Real patients, real results. See the life-changing transformations.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {SENIOR_SHOWCASE.map((item) => (
                <div key={item.id} className="group" data-testid={`senior-item-${item.id}`}>
                  <div className="relative overflow-hidden rounded-2xl shadow-lg">
                    <img
                      src={item.image}
                      alt={item.description}
                      className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform">
                      <div className="flex justify-between text-white text-sm font-medium">
                        <span className="bg-slate-800/80 px-3 py-1 rounded-full">Before</span>
                        <span className="bg-orange-600/90 px-3 py-1 rounded-full">After</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {item.description}
                    </h3>
                    <span className="inline-block mt-2 text-xs bg-orange-100 text-orange-700 px-3 py-1 rounded-full">
                      {item.category}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Disclaimer */}
      <section className="py-8 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-slate-500">
            Individual results may vary. These images represent actual patient cases and are shared with patient consent. 
            Your results will depend on your specific situation and treatment plan.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-orange-600" data-testid="gallery-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready for Your Transformation?
          </h2>
          <p className="mt-4 text-xl text-orange-100">
            Schedule a consultation to discuss your goals and see what's possible.
          </p>
          <Button
            onClick={() => setShowAppointment(true)}
            className="mt-10 bg-white text-orange-600 hover:bg-orange-50 rounded-full px-8 py-6 text-lg shadow-xl transition-all hover:scale-105"
            data-testid="gallery-schedule-btn"
          >
            Schedule Consultation
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <AppointmentModal open={showAppointment} onOpenChange={setShowAppointment} />
    </div>
  );
}
